import React, {useEffect, useMemo, useRef, useState} from 'react';
import {CButton, CInput, CInputCheckbox} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import {Fluxo, Modulo} from "./modelo";
import {ICONES_MODULO, iconeDoModulo} from "./iconesModulo";

/**
 * Escolha do icone do modulo (#484909): o botao mostra o icone que o portal vai
 * usar (o automatico, pelo nome, fica apagado); a grade abre embaixo.
 */
const IconePicker = ({modulo, desabilitado, onEscolher}: {modulo: Modulo, desabilitado: boolean, onEscolher: (icone: string | null) => void}) => {
    // Posicao fixa (a tabela rola de lado e cortaria a grade); null = fechada.
    const [aberto, setAberto] = useState<{top: number, left: number} | null>(null);
    const caixa = useRef<HTMLDivElement>(null);
    const atual = iconeDoModulo(modulo.nome, modulo.icone);

    // Clique fora ou Esc fecha.
    useEffect(() => {
        if (!aberto) return;
        const fora = (e: MouseEvent) => { if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(null); };
        const tecla = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(null);
        const rolou = () => setAberto(null);
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', tecla);
        window.addEventListener('scroll', rolou, true);
        return () => {
            document.removeEventListener('mousedown', fora);
            document.removeEventListener('keydown', tecla);
            window.removeEventListener('scroll', rolou, true);
        };
    }, [aberto]);

    const escolhe = (icone: string | null) => {
        onEscolher(icone);
        setAberto(null);
    };

    return (
        <div className="fx-icone" ref={caixa}>
            <button type="button" className={`fx-icone-botao ${modulo.icone ? '' : 'automatico'}`} disabled={desabilitado}
                    onClick={e => {
                        const caixaBotao = e.currentTarget.getBoundingClientRect();
                        setAberto(aberto ? null : {top: caixaBotao.bottom + 4, left: caixaBotao.left});
                    }} aria-expanded={!!aberto}
                    title={modulo.icone ? 'Ícone do módulo no portal do cliente' : 'Automático, pelo nome do módulo: clique para escolher'}>
                <CIcon name={atual}/>
            </button>
            {aberto && (
                <div className="fx-icone-grade" role="listbox" aria-label="Ícone do módulo" style={{top: aberto.top, left: aberto.left}}>
                    <button type="button" className={`fx-icone-auto ${modulo.icone ? '' : 'ativo'}`} onClick={() => escolhe(null)}>
                        Automático (pelo nome)
                    </button>
                    {ICONES_MODULO.map(([icone, rotulo]) => (
                        <button type="button" key={icone} role="option" aria-selected={modulo.icone === icone} title={rotulo} aria-label={rotulo}
                                className={modulo.icone === icone ? 'ativo' : ''} onClick={() => escolhe(icone)}>
                            <CIcon name={icone}/>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

interface Props {
    fluxo: Fluxo,
    somenteLeitura: boolean,
    onMudar: (modulos: Modulo[], renomeado?: {de: string, para: string}) => void,
}

/**
 * Modulos do fluxo: a tag funcional dos itens. Modulo opcional so gera os
 * itens no processo quando marcado na abertura (o que o cliente contratou).
 */
const ModulosTab = ({fluxo, somenteLeitura, onMudar}: Props) => {
    const ro = somenteLeitura;

    const uso = useMemo(() => {
        const contagem: {[codigo: string]: number} = {};
        fluxo.etapas.forEach(etapa => etapa.itens.forEach(item => {
            if (item.modulo) {
                contagem[item.modulo] = (contagem[item.modulo] || 0) + 1;
            }
        }));
        return contagem;
    }, [fluxo.etapas]);

    const muda = (indice: number, campo: keyof Modulo, valor: any) => {
        const antigo = fluxo.modulos[indice];
        const modulos = fluxo.modulos.map((m, i) => (i === indice ? {...m, [campo]: valor} : m));
        const codigoRepetido = campo === 'codigo' && fluxo.modulos.some((m, i) => i !== indice && m.codigo === valor);
        onMudar(modulos, campo === 'codigo' && !codigoRepetido ? {de: antigo.codigo, para: valor} : undefined);
    };

    const move = (indice: number, direcao: -1 | 1) => {
        const modulos = [...fluxo.modulos];
        const [m] = modulos.splice(indice, 1);
        modulos.splice(indice + direcao, 0, m);
        onMudar(modulos);
    };

    return (
        <div className="fx-aba">
            <div className="fx-aba-conteudo">
                <p className="text-muted small mb-3">
                    Módulo é a área funcional do item (Fiscal, Marketplace…). Os opcionais aparecem na abertura do processo para
                    marcar o que o cliente contratou; itens de módulo opcional não marcado não são gerados. O ícone aparece no
                    cartão do módulo, na timeline do portal do cliente (sem escolher, vai pelo nome).
                </p>
                <div className="fx-tabela">
                    <div className="fx-tabela-cabecalho modulos">
                        <span>Ícone</span><span>Código</span><span>Nome</span><span>Opcional</span><span>Itens</span><span/>
                    </div>
                    {fluxo.modulos.map((modulo, i) => (
                        <div className="fx-tabela-linha modulos" key={modulo.id ?? `novo-${i}`}>
                            <IconePicker modulo={modulo} desabilitado={ro} onEscolher={icone => muda(i, 'icone', icone)}/>
                            <CInput size="sm" value={modulo.codigo} maxLength={20} disabled={ro} placeholder="MKP"
                                    onChange={(e: any) => muda(i, 'codigo', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}/>
                            <CInput size="sm" value={modulo.nome} maxLength={120} disabled={ro} placeholder="Marketplace"
                                    onChange={(e: any) => muda(i, 'nome', e.target.value)}/>
                            <span className="fx-centro">
                                <CInputCheckbox className="position-static m-0" checked={modulo.opcional} disabled={ro}
                                                onChange={(e: any) => muda(i, 'opcional', e.target.checked)}/>
                            </span>
                            <span className="fx-uso">{uso[modulo.codigo] || 0}</span>
                            <span className="fx-botoes">
                                {!ro && (
                                    <>
                                        <CButton size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} title="Subir"><CIcon name="cil-arrow-top"/></CButton>
                                        <CButton size="sm" variant="ghost" disabled={i === fluxo.modulos.length - 1} onClick={() => move(i, 1)} title="Descer"><CIcon name="cil-arrow-bottom"/></CButton>
                                        <CButton size="sm" variant="ghost" color="danger" disabled={!!uso[modulo.codigo]}
                                                 onClick={() => onMudar(fluxo.modulos.filter((_, j) => j !== i))}
                                                 title={uso[modulo.codigo] ? 'Em uso: tire o módulo dos itens antes' : 'Excluir'}>
                                            <CIcon name="cil-trash"/>
                                        </CButton>
                                    </>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
                {!ro && (
                    <CButton size="sm" color="success" variant="outline" className="mt-2"
                             onClick={() => onMudar([...fluxo.modulos, {codigo: '', nome: '', opcional: true, icone: null}])}>
                        <CIcon name="cil-plus"/> Módulo
                    </CButton>
                )}
            </div>
        </div>
    );
};

export default ModulosTab;
